import React from 'react';
import { Tag, Tooltip, Popconfirm } from 'antd';
import type { MerchantDomain, DomainCardProps } from '../types';
import { PLATFORMS, DOMAIN_STATUS_CONFIG, type PlatformKey } from '../constants';
import CopyableCode from './CopyableCode';
import {
  DomainCardWrapper,
  DomainCardTopRow,
  DomainCardMetaRow,
  DomainName,
  DnsInfoRow,
  DnsPropagationHint,
  PlatformTagStyled,
  DomainGlobalIcon,
  DomainDeleteIcon,
  DomainCardAlert,
} from '../Styles';

const StatusTag: React.FC<{ status: MerchantDomain['status'] }> = ({ status }) => {
  const { color, label, Icon } = DOMAIN_STATUS_CONFIG[status] ?? DOMAIN_STATUS_CONFIG.pending;
  return <Tag icon={<Icon />} color={color}>{label}</Tag>;
};

const PlatformTag: React.FC<{ platform: PlatformKey }> = ({ platform }) => {
  const cfg = PLATFORMS[platform] || PLATFORMS.custom;
  return <PlatformTagStyled $bgColor={cfg.color}>{cfg.label}</PlatformTagStyled>;
};

const DomainCard: React.FC<DomainCardProps> = ({ domain, onRemove, removing }) => {
  const platform = (domain.platform as PlatformKey) || 'custom';
  const cfg = PLATFORMS[platform] || PLATFORMS.custom;
  const dnsTarget = domain.dnsTarget || cfg.dnsHint;
  const recordType = domain.recordType || cfg.recordType || 'CNAME';

  return (
    <DomainCardWrapper>
      <DomainCardTopRow>
        <DomainCardMetaRow>
          <DomainGlobalIcon />
          <DomainName>{domain.domain}</DomainName>
          <PlatformTag platform={platform} />
          <StatusTag status={domain.status} />
        </DomainCardMetaRow>
        <Popconfirm
          title="Remove this domain?"
          description="DNS records you've set will remain — remove them manually from your provider."
          onConfirm={() => onRemove(domain.id)}
          okText="Remove"
          okButtonProps={{ danger: true }}
          cancelText="Cancel"
        >
          <Tooltip title="Remove domain">
            <DomainDeleteIcon $removing={removing} />
          </Tooltip>
        </Popconfirm>
      </DomainCardTopRow>

      {domain.status !== 'verified' && dnsTarget && (
        <DomainCardAlert
          type="warning"
          showIcon
          message={`Add a ${recordType} record in your domain provider`}
          description={
            <div>
              <DnsInfoRow>
                <span><strong>Type:</strong> {recordType}</span>
                <span><strong>Name:</strong> @ (root) or subdomain</span>
              </DnsInfoRow>
              <strong>Value:</strong>
              <CopyableCode value={dnsTarget} />
              <DnsPropagationHint>DNS propagation can take up to 24–48 hours.</DnsPropagationHint>
            </div>
          }
        />
      )}

      {domain.status === 'failed' && (
        <DomainCardAlert
          type="error"
          showIcon
          message="Domain verification failed. Check your DNS records and try again."
        />
      )}
    </DomainCardWrapper>
  );
};

export default DomainCard;
